/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AdminTable from '../../../components/AdminTable';
import DashboardHeaderWithFilters from '../../../components/DashboardHeaderWithFilters';
import EmptyRecord from '../../../components/EmptyRecord';
import useFilterMethods from '../../../customHooks/useFilterMethods';
import useGetQueryParams from '../../../customHooks/useGetQueryParams';
import useTableColumn from '../../../customHooks/useTableColumn';
import useToast from '../../../customHooks/useToast';
import { GET_KAABO_PARTNERS } from '../../../graphql/admin_queries';
import useCustomQuery from '../../../network/useCustomQuery';
import EmptyMerchantSettlement from '../../../svgs/EmptyMerchantSettlement';
import ListIcon from '../../../svgs/ListIcon';
import generateCSV from '../../../utils/generateCSV';
import generatePDF from '../../../utils/generatePdf';
import { cleanUpPartnerCode } from '../../../utils/helpers';
import { AdminRouteName } from '../../CONSTANTS';

import Styles from './PartnerTrackingRecordSection.module.scss';
import GeneratePartnerTrackingCodeModal from '../GeneratePartnerTrackingCode';
import ActivatePartner from '../ActivatePartner';
import DeactivatePartner from '../DeactivatePartner';

const limit = 14;
interface IPartnerTrackingRecordSection{
  isShowGenerateCodeModal:boolean;
  setIsShowGenerateCodeModal:any;
}
const PartnerTrackingRecordSection = ({ isShowGenerateCodeModal, setIsShowGenerateCodeModal }:IPartnerTrackingRecordSection) => {
  const Toast = useToast();
  const [getKaaboPartners, getKaaboPartnersLoading, kaaboPartners] = useCustomQuery({
    query: GET_KAABO_PARTNERS,
    onError: () => {},
    onData: () => {},
  });
  const { getQuery, getFilterQueries } = useGetQueryParams();
  const search = getQuery('search') || '';
  const page = Number(getQuery('page'));
  const { push } = useHistory();

  const [selectedMember, setSelectedMember] = useState(null);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  const allColumns = [
    ['image', 'email', 'hasBottomValue', 'name'],
    'code',
    'totalReferrals',
    'status',
    'date',
  ];
  const columnLabelFormat = {
    name: 'Partner Name',
    code: 'Tracking Code',
    totalReferrals: 'No. of Referrals',
    status: 'Status',
    date: 'Date Created',
  };
  const { getColumns } = useTableColumn(allColumns, 'reward');

  const [partnersRec, setPartnersRec] = useState(null);
  const setPage = (pageNum) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', pageNum);
    push({
      search: searchParams.toString(),
    });
  };
  if (!page) {
    push(`/${AdminRouteName?.rewardpartnersTrackingCode}?page=1&search=${search}`);
  }

  const onSearchChange = (e) => {
    const result = getFilterQueries();
    push(`${AdminRouteName?.rewardpartnersTrackingCode}?page=1&search=${encodeURIComponent(e.target.value)}&fromDateTable=${result?.fromDateTable}&toDateTable=${result?.toDateTable}`);
  };

  useEffect(() => {
    if (page) {
      const result = getFilterQueries();
      getKaaboPartners({
        variables: {
          offset: (page - 1) * limit,
          limit,
          search,
          from: result?.fromDateTable,
          to: result?.toDateTable,
        },
      });
    }
  }, [page, search]);

  const getPartnersFunc = (result) => {
    getKaaboPartners({
      variables: {
        offset: 0,
        limit,
        search,
        from: result?.fromDateTable,
        to: result?.toDateTable,
      },
    });
  };

  const onFilterSubmit = (result) => {
    getPartnersFunc(result);
  };

  const {
    filterObj,
  } = useFilterMethods({
    allowedFilters: ['tableDateRange'],
    defaultUrl: `/${AdminRouteName?.rewardpartnersTrackingCode}?page=1`,
    cb: onFilterSubmit,
    Toast,
  });

  useEffect(() => {
    if (kaaboPartners?.getKaaboPartners?.data) {
      setPartnersRec(kaaboPartners?.getKaaboPartners?.data);
    }
  }, [kaaboPartners]);

  const [getExportRecord] = useCustomQuery({
    query: GET_KAABO_PARTNERS,
    onError: () => {},
    onData: () => {},
  });

  const handleExport = async (type: string) => {
    try {
      const result = getFilterQueries();
      const output = await getExportRecord({
        variables: {
          offset: 0,
          limit: 2000,
          search,
          from: result?.fromDateTable,
          to: result?.toDateTable,
        },
      });

      if (output?.data?.getKaaboPartners?.data) {
        const rec = cleanUpPartnerCode(output?.data?.getKaaboPartners?.data?.kaaboPartners);
        const func = type === 'pdf' ? generatePDF : generateCSV;
        const selectedHeaders = [
          'S/N',
          // change image__email__hasBottomValue__name concated columns to a single column of name
          ...(getColumns?.()?.map((column) => (column === 'image__email__hasBottomValue__name' ? ['name', 'email'] : column)) || []),
        ];

        func({
          payload: rec,
          title: 'Partner Tracking Code',
          selectedHeaders,
          formatHeaders: columnLabelFormat,
          cb: () => Toast.success('Exported successfully'),
        });
      }
    } catch (error) {
      Toast.error('Error while exporting');
    }
  };

  const handleRoute = (data:any) => {
    push(`/rewards/partner-details/${data?.id}`);
  };

  const handleSelectDropdown = (data, details) => {
    if (data === 'Activate') {
      setSelectedMember(details);
      setShowActivateModal(true);
      return;
    }
    if (data === 'Deactivate') {
      setSelectedMember(details);
      setShowDeactivateModal(true);
      return;
    }
    push(`/rewards/partner-details/${details?.id}`);
  };

  return (
    <div className={Styles.wrapper}>
      <div className={Styles.wrapper__header}>
        <DashboardHeaderWithFilters
          title="Partner's Code"
          filterObj={filterObj}
          dateType="tableDateRange"
          showSearch
          search={search}
          onSearchChange={onSearchChange}
          searchPlaceholder="Partner name / Email address"
          exportObj={{
            handleExport,
          }}
        />
      </div>

      <div className="transaction-table-details-cover">
        {partnersRec?.count ? (
          <AdminTable
            refreshing={getKaaboPartnersLoading}
            page={page}
            setPage={setPage}
            pageSize={limit}
            minHeight="calc(100vh - 280px)"
            count={partnersRec?.count}
            contents={cleanUpPartnerCode(partnersRec?.kaaboPartners)}
            reference="id"
            hideDominantImage
            hasNameAndImage
            clickable
            handleClick={handleRoute}
            allowKeyArr={getColumns(true)}
            mobileAllowKeyArr={
              [
                'status',
                ['name', 'email'],
                ['code', 'date'],
              ]
            }
            handleDropdown={handleSelectDropdown}
            formatLabels={columnLabelFormat}
            dropdownType="partner"
            dropDownIcon={<div className={Styles.wrapper__icon}><ListIcon /></div>}
          />
        ) : (
          <EmptyRecord
            height="calc(100vh - 240px)"
            title={getKaaboPartnersLoading ? 'Fetching Partner Code...' : 'No Partner Code yet'}
            main="All Partner Codes on Payfi will appear here"
            loading={getKaaboPartnersLoading}
            Icon={EmptyMerchantSettlement}
          />
        )}

      </div>
      {
        isShowGenerateCodeModal && <GeneratePartnerTrackingCodeModal refetch={getPartnersFunc} closeModal={() => setIsShowGenerateCodeModal(false)} />
      }

      {
        showActivateModal && selectedMember && <ActivatePartner payload={selectedMember} onClose={() => setShowActivateModal(false)} refetch={getPartnersFunc} />
      }
      {
        showDeactivateModal && selectedMember && <DeactivatePartner payload={selectedMember} onClose={() => setShowDeactivateModal(false)} refetch={getPartnersFunc} />
      }

    </div>
  );
};

export default PartnerTrackingRecordSection;
